/**
 * Service-worker registration (spec SP3, cue-forge-kxt.2). Production-only: in
 * dev/tests there is no built /sw.js and we must not cache unhashed dev assets.
 * When a new worker finishes installing while an old one still controls the page,
 * `onUpdate(apply)` fires; calling `apply()` activates the new worker and reloads
 * once it takes control. First installs do NOT reload (no controllerchange
 * listener is attached until the user opts in).
 */
export type ApplyUpdate = () => void;

export function registerServiceWorker(onUpdate: (apply: ApplyUpdate) => void): void {
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !import.meta.env.PROD
  ) {
    return;
  }
  const sw = navigator.serviceWorker;
  const register = () => {
    void sw
      .register('/sw.js')
      .then((reg) => {
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // A new worker is installed and waiting while an old one still
            // controls the page — that's an update the user can apply.
            if (installing.state === 'installed' && sw.controller) {
              onUpdate(() => {
                sw.addEventListener('controllerchange', () => window.location.reload(), {
                  once: true,
                });
                installing.postMessage({ type: 'SKIP_WAITING' });
              });
            }
          });
        });
      })
      .catch(() => {
        /* offline support is best-effort */
      });
  };

  // Register after load to stay off the critical path — but if the page has
  // already finished loading (effects run post-paint), register now so we don't
  // miss the event entirely.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}
